import {
  Error,
  SelectList,
} from "app/based/baseApiModel/baseApiResponseModel/BaseApiResponseModel";
import { BaseFilterListRequestModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListRequestModel";
import { BaseFilterListResponseModel } from "app/based/baseApiModel/baseFilterListModel/BaseFilterListResponseModel";
import { BaseUserRequestModel } from "app/based/baseApiModel/baseUserModel/BaseUserRequestModel";
import { BaseUserResponseModel } from "app/based/baseApiModel/baseUserModel/BaseUserResponseModel";
import { BaseUserItemResponseModel } from "app/based/baseApiModel/baseUserModel/userItem/BaseUserItemResponseModel";
import { BasedObservedViewModel } from "app/based/basedObservedViewModel/BasedObservedViewModel";
import { IOTSimRegistrationInjection } from "features/iotSimRegistration/di/IOTSimRegistrationInjection";
import { IOTSimRegistrationQueryEntity } from "features/iotSimRegistration/domain/entities/IOTSimRegistrationEntity";
import isEmpty from "lodash/isEmpty";
import isString from "lodash/isString";
import { BehaviorSubject } from "rxjs";
import { injectable } from "tsyringe";
import { productCache } from "utils/cache/productCache";
import { CacheKeyEnum } from "utils/enums/CacheKeyEnum";
import { strings } from "utils/localizedStrings";
import { IIOTSimRegistrationObservedState } from "../interfaces/IIOTSimRegistration";

type PackageFetchMode = "reset" | "append";

type PackageCommand =
  | {
      action: "fetch";
      params: BaseFilterListRequestModel;
      mode: PackageFetchMode;
    }
  | { action: "restore" }
  | { action: "clear" }
  | { action: "idle" };

class IOTSimRegistrationInput implements Disposable {
  public fetchList = new BehaviorSubject<IOTSimRegistrationQueryEntity>({});
  public fetchCarrier = new BehaviorSubject<BaseFilterListRequestModel>({});
  public fetchPackage = new BehaviorSubject<PackageCommand>({
    action: "idle",
  });
  public exportExcel = new BehaviorSubject<IOTSimRegistrationQueryEntity>({});
  public fetchDetail = new BehaviorSubject<IOTSimRegistrationQueryEntity>({});
  public checkStatus = new BehaviorSubject<string>("");
  public fetchPartners = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStores = new BehaviorSubject<BaseFilterListRequestModel>({});
  public loadStaff = new BehaviorSubject<BaseUserRequestModel>({});
  public loadUsers = new BehaviorSubject<BaseUserRequestModel>({});

  [Symbol.dispose](): void {
    this.fetchList.complete();
    this.fetchCarrier.complete();
    this.fetchPackage.complete();
    this.exportExcel.complete();
    this.fetchDetail.complete();
    this.checkStatus.complete();
    this.fetchPartners.complete();
    this.loadStores.complete();
    this.loadStaff.complete();
    this.loadUsers.complete();
  }
}

class IOTSimRegistrationOutput implements Disposable {
  public onError = new BehaviorSubject<Error>({});
  public onExportSuccess = new BehaviorSubject<Blob | null>(null);
  public onCheckStatusSuccess = new BehaviorSubject<boolean>(false);
  public onGetDetailSuccess = new BehaviorSubject<boolean>(false);

  [Symbol.dispose](): void {
    this.onError.complete();
    this.onExportSuccess.complete();
    this.onCheckStatusSuccess.complete();
    this.onGetDetailSuccess.complete();
  }
}

@injectable()
export class IOTSimRegistrationViewModel extends BasedObservedViewModel<
  IOTSimRegistrationInput,
  IOTSimRegistrationOutput,
  IIOTSimRegistrationObservedState
> {
  private iotSimRegistrationUseCase =
    IOTSimRegistrationInjection.getIOTSimRegistrationUseCase();

  constructor() {
    super(new IOTSimRegistrationInput(), new IOTSimRegistrationOutput());
  }

  protected override createObservedState(): IIOTSimRegistrationObservedState {
    return {
      list: [],
      detail: null,
      pagination: { page: 0, total: 0, limit: 10 },
      loading: false,
      detailLoading: false,
      statusLoading: {},
      carrier: [],
      packageList: [],
      defaultPackageList: [],
      packageLoading: false,
      packageHasMore: true,
      partnerOptions: [],
      storeOptions: [],
      staffOptions: [],
      userOptions: [],
      summary: {},
    };
  }

  protected override transform(): void {
    this.skipFirstInitial(this.input.fetchList, async (params) => {
      try {
        this.setObservedState({ loading: true, list: [], summary: {} });
        const res = await this.iotSimRegistrationUseCase.getList(params);
        if (res?.data) {
          this.setObservedState({
            list: res.data,
            pagination: res.pagination,
            summary: !res?.summary
              ? {
                  error: res?.pagination?.error,
                  finished: res?.pagination?.success,
                  other: res?.pagination?.other,
                  revenue: isString(res?.pagination?.revenue)
                    ? {
                        VND: Number(res?.pagination?.revenue as string),
                        USD: 0,
                      }
                    : { VND: 0, USD: 0 },
                }
              : res?.summary,
          });
        }
      } catch (error) {
        console.error(error);
        this.setObservedState({ list: [], summary: {} });
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    this.skipFirstInitial(this.input.fetchCarrier, async (params) => {
      try {
        const res = await this.iotSimRegistrationUseCase.getFilterList(
          params,
          "carrier"
        );
        const carrier = (res ?? []).filter(
          (item) =>
            item?.code === "viettel" ||
            item?.code === "mobifone" ||
            item?.code === "vinaphone"
        );
        this.setObservedState({ carrier });
      } catch (error) {
        console.error(error);
        this.setObservedState({ carrier: [] });
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.fetchPackage, async (command, guard) => {
      if (command.action === "restore") {
        const { defaultPackageList } = this.observedState;
        this.setObservedState({
          packageList: defaultPackageList,
          packageLoading: false,
          packageHasMore: defaultPackageList.length >= 100,
        });
        return;
      }

      if (command.action === "clear") {
        this.setObservedState({
          packageList: [],
          defaultPackageList: [],
          packageLoading: false,
          packageHasMore: true,
        });
        return;
      }

      if (command.action !== "fetch") return;

      const { params, mode } = command;
      try {
        this.setObservedState({ packageLoading: true });
        const res = await this.iotSimRegistrationUseCase.getFilterList(
          params,
          "package"
        );
        const responsePackageList = Array.from(
          new Map((res ?? []).map((item) => [item.code, item])).values()
        );

        guard?.runIfLatest(() => {
          const nextPackageList =
            mode === "append"
              ? [...this.observedState.packageList, ...responsePackageList]
              : responsePackageList;
          const packageList = Array.from(
            new Map(nextPackageList.map((item) => [item.code, item])).values()
          );
          const isDefaultRequest =
            mode === "reset" &&
            (params.page ?? 0) === 0 &&
            !params.code;

          this.setObservedState({
            packageList,
            packageHasMore:
              responsePackageList.length >= (params.limit ?? 100),
            ...(isDefaultRequest
              ? { defaultPackageList: responsePackageList }
              : {}),
          });
        });
      } catch (error) {
        console.error(error);
        guard?.runIfLatest(() => this.output.onError.next(error));
      } finally {
        guard?.runIfLatest(() =>
          this.setObservedState({ packageLoading: false })
        );
      }
    });

    this.skipFirstInitial(this.input.fetchDetail, async (params) => {
      try {
        this.setObservedState({ detailLoading: true });
        const res = await this.iotSimRegistrationUseCase.getDetail(params);
        if (res?.data) {
          this.setObservedState({ detail: res.data });
          this.output.onGetDetailSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ detailLoading: false });
      }
    });

    this.skipFirstInitial(this.input.exportExcel, async (params) => {
      try {
        this.setObservedState({ loading: true });
        const res = await this.iotSimRegistrationUseCase.exportReport(params);
        if (res?.data) {
          this.output.onExportSuccess.next(res.data);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setObservedState({ loading: false });
      }
    });

    this.skipFirstInitial(this.input.checkStatus, async (code) => {
      if (!code) return;
      try {
        this.setStatusLoading(code, true);
        const res = await this.iotSimRegistrationUseCase.checkStatus(code);
        if (res?.data) {
          this.output.onCheckStatusSuccess.next(true);
        }
      } catch (error) {
        console.error(error);
        this.output.onError.next(error);
      } finally {
        this.setStatusLoading(code, false);
      }
    });

    this.skipFirstInitial(this.input.fetchPartners, async (params) => {
      try {
        const cache = productCache.getCache(CacheKeyEnum.partnerList);
        if (!isEmpty(cache)) {
          this.setObservedState({
            partnerOptions: this.mapPartnerOptions(cache),
          });
          return;
        }

        const res = await this.iotSimRegistrationUseCase.getFilterList(
          params,
          "partner"
        );
        const data = res ?? [];
        this.setObservedState({
          partnerOptions: this.mapPartnerOptions(data),
        });
        productCache.saveCache({
          key: CacheKeyEnum.partnerList,
          product: data,
        });
      } catch (error) {
        console.error("Error fetching partners:", error);
        this.setObservedState({ partnerOptions: [] });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadStores, async (params) => {
      const { partnerCode } = params;
      if (!partnerCode) {
        this.setObservedState({ storeOptions: this.allOption() });
        return;
      }

      try {
        const res = await this.iotSimRegistrationUseCase.getFilterList(
          params,
          "store"
        );
        this.setObservedState({
          storeOptions: [
            ...this.allOption(),
            ...(res ?? []).map((store) => ({
              label: store.store_name,
              value: store.store_code,
            })),
          ],
        });
      } catch (error) {
        this.setObservedState({ storeOptions: this.allOption() });
        this.output.onError.next(error);
      }
    });

    this.skipFirstInitial(this.input.loadStaff, async (params) => {
      if (isEmpty(params?.storeCode)) {
        this.setObservedState({ staffOptions: this.allOption() });
        return;
      }

      try {
        const res = await this.iotSimRegistrationUseCase.getUserForFilter(
          params,
          "staff"
        );
        const users = this.flattenUsers(res);
        this.setObservedState({
          staffOptions: [
            ...this.allOption(),
            ...users.map((user) => ({
              label:
                user.name || user.userName || user.email || user.phone || "",
              value: user.id,
            })),
          ],
        });
      } catch (error) {
        this.setObservedState({ staffOptions: this.allOption() });
        this.output.onError.next(error as Error);
      }
    });

    this.skipFirstInitial(this.input.loadUsers, async (params) => {
      const { providerCode } = params;
      try {
        const target =
          providerCode === "mobitopup" || providerCode === "smm"
            ? "staff"
            : "userB2B";
        const res = await this.iotSimRegistrationUseCase.getUserForFilter(
          params,
          target
        );
        const users = this.flattenUsers(res);
        this.setObservedState({
          userOptions: [
            ...this.allOption(),
            ...users.map((user) => ({
              label:
                user.fullName || user.email || user.name || user.phone || "",
              value: user.id,
            })),
          ],
        });
      } catch (error) {
        this.setObservedState({ userOptions: this.allOption() });
        this.output.onError.next(error as Error);
      }
    });
  }

  private allOption(): SelectList[] {
    return [{ label: strings.GlobalLabels.All, value: "" }];
  }

  private mapPartnerOptions(
    partners: BaseFilterListResponseModel[]
  ): SelectList[] {
    return [
      ...this.allOption(),
      ...(partners ?? []).map((partner) => ({
        label: `${partner.name}${
          partner.providerCode ? ` - ${partner.providerCode.toUpperCase()}` : ""
        }`,
        value: partner.code,
        type: partner.type,
        isStore: partner.isStore,
        providerCode: partner.providerCode,
      })),
    ];
  }

  private flattenUsers(
    users: BaseUserResponseModel[]
  ): BaseUserItemResponseModel[] {
    return (users ?? []).flatMap((item) => item?.users ?? []);
  }

  private setStatusLoading(code: string, loading: boolean): void {
    this.setObservedState((state) => ({
      ...state,
      statusLoading: {
        ...state.statusLoading,
        [code]: loading,
      },
    }));
  }
}
